import { useState } from "react";
import { toast } from "sonner";
import { KeyRound, Trash2 } from "lucide-react";
import { api, errorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

export const HostAccountPanel = ({ member, account, onChanged }) => {
  const [email, setEmail] = useState(account?.email || "");
  const [password, setPassword] = useState("");
  const [canPublish, setCanPublish] = useState(account ? account.can_publish : true);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!email) return toast.error("Enter the host's login email");
    if (!account && password.length < 8) return toast.error("Password must be at least 8 characters");
    if (password && password.length < 8) return toast.error("Password must be at least 8 characters");
    setBusy(true);
    try {
      await api.post(`/admin/team/${member.id}/account`, { email, password: password || null, can_publish: canPublish });
      toast.success(account ? "Login updated" : "Login created — share the email and password with the host");
      setPassword(""); onChanged();
    } catch (e) { toast.error(errorMessage(e)); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Remove ${member.name}'s login? They will no longer be able to sign in.`)) return;
    setBusy(true);
    try { await api.delete(`/admin/team/${member.id}/account`); toast.success("Login removed"); setEmail(""); setPassword(""); onChanged(); }
    catch (e) { toast.error(errorMessage(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-xl bg-white/[0.03] border border-white/10 p-4 space-y-3" data-testid="host-account-panel">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="eyebrow flex items-center gap-1.5"><KeyRound size={12} /> Publishing login</p>
          <p className="text-xs text-slate-500 mt-1">{account ? `Signed in ${account.last_login_at ? "recently" : "never"} · hosts land on the Publish page and only see their assigned shows.` : "Give this host a login so they can publish episodes to their assigned shows from /admin/login."}</p>
        </div>
        <label className="flex items-center gap-2 shrink-0 text-xs font-semibold"><Switch checked={canPublish} onCheckedChange={setCanPublish} data-testid="host-account-can-publish" /> Can publish</label>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div><Label>Login email</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="host@example.com" className="mt-1.5 bg-white/5 border-white/10" data-testid="host-account-email" /></div>
        <div><Label>{account ? "New password (leave blank to keep)" : "Password"}</Label><Input type="text" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 8 characters" className="mt-1.5 bg-white/5 border-white/10 font-mono" data-testid="host-account-password" /></div>
      </div>
      <div className="flex justify-end gap-2">
        {account && <Button type="button" variant="ghost" onClick={remove} disabled={busy} className="text-rose-400 hover:text-rose-300" data-testid="host-account-remove"><Trash2 size={14} className="mr-1" /> Remove login</Button>}
        <Button type="button" onClick={save} disabled={busy} variant="outline" className="rounded-full border-white/15 bg-transparent hover:bg-white/10" data-testid="host-account-save">{busy ? "Saving…" : account ? "Update login" : "Create login"}</Button>
      </div>
    </div>
  );
};
